import { SaneInput } from 'components/SaneInput';
import { AndClause, ClauseDispatcher, ColumnClause, FilterClause, LiteralClause, OperatorClause, OrClause, RootClause, SearchClause } from 'query/Filter';
import Query from 'query/Query';
import React from 'react';
import '../DataTable.css';
import { ColumnType } from 'query/ColumnType';

export interface FilterProps {
    /** If query is undefined, then I'm a bit gray.
     * If query is a RootQuery, then I enable "Search:" as a column option.
     */
    query: Query;
    clause: FilterClause;
    ghost?: boolean;
    onChanged: () => void;
}

export interface FilterState {

}

/* This is a rudimentary implementation for a filter for now.
 A full implementation would have:
 * All of this would be a tree of and/or/not operators, or a generalised "Search:" text value.
 * Either "Search:" or a column-path drop-down. The column-path drop-down would be a DataTable with all its features.
 * A column-dependent list of actions:
   - For ords (number, date, boolean, ): =, <, >, <=, >=, !=, in.
   - For text: =, contains, like, similar to, >, <.
   - For GIS fields, various fancy operators.
 * A column-dependent value, or another column-path.

   TODO: Maybe it's worthwhile merging my "model" into me so that the state is stored in react components?
   Maybe I could inherit from the model?
*/
export class Filter extends React.Component<FilterProps, FilterState>  {
    constructor(props: Readonly<FilterProps>) {
        super(props);
        this.state = {
        };

    }

    render = () => {
        let renderer = new FilterRenderer(this);
        this.props.clause.visit(renderer);
        // TODO: add keys to the children of this map.
        return renderer.acc;
    }

    removeNode: (v: OperatorClause) => void
        = (v) => {
            if (undefined !== this.props.clause) {
                this.props.query?.removeClause(v);
            }
            this.onChanged();
        }

    get query(): Query {
        return this.props.query;
    }

    onSearchValueChanged(v: SearchClause, s: string): void {
        v.value = s;
        this.props.onChanged();
    }

    onColumnChange(columnName: string, textFieldValue: string): void {
        if (SearchClause.ID_SEARCHCLAUSE === textFieldValue) {
            throw new Error("Not sure how you did that.")
        }

        let column = this.query.findTableColumnByPath(columnName);
        if (null == column) {
            alert("Column not found...? This isn't an error that you should be seeing.")
            return
        }
        this.query.replaceFilter(column, textFieldValue);
        this.props.onChanged();
    }

    onChanged(): void {
        this.props.onChanged();
    }

}

/* I'm a visitor acceptor, used to render the query. 

If you haven't seen this pattern before it works as follows:
1. The Filter component, above, instantiates and owns me.
2. I get passed through the whole filter tree. 
3. I'm invoked by each node. As I'm invoked, I accumulate a list of renderables that are rendered by Filter.

Most of the event handlers and functionality should be in Filter 
where there is access to the query and UI stuff. */
class FilterRenderer implements ClauseDispatcher {
    constructor(filter: Filter) {
        this.filter = filter;
        this._key = 0;
    }

    acc: Array<JSX.Element> = []; // "Accumulator"
    private filter: Filter;
    private _key: number;

    private nextKey = () => {
        return this._key++;
    }

    acceptRoot = (v: RootClause) => {
        v.clause.visit(this);
    }

    acceptSearch = (v: SearchClause) => {
        this.renderColumnChoice(v, true);
        /* Commenting this out for now as I haven't yet found an OData server implementing $search.
        this.acc.push(<SaneInput key={this.nextKey()} value={v.value} onCommit={(s) => this.filter.onSearchValueChanged(v, s)}></SaneInput>);
        */
    }

    acceptAnd = (v: AndClause) => {
        v.left.visit(this);
        this.acc.push(<React.Fragment key={this.nextKey()}>And</React.Fragment>);
        v.right.visit(this);
    }

    acceptOr = (v: OrClause) => {
        v.left.visit(this);
        this.acc.push(<React.Fragment key={this.nextKey()}>Or</React.Fragment>)
        v.right.visit(this);
    }

    acceptOperator = (v: OperatorClause) => {
        v.left.visit(this);

        this.acc.push(
            <select key={this.nextKey()} className='datatable-filter-select'>
                <option>=</option>
                <option>&gt;</option>
                <option>&lt;</option>
            </select>
        )

        v.right.visit(this);
        this.acc.push(
            <button key={this.nextKey()} onClick={() => this.filter.removeNode(v)} className={"datatable-filter-removeButton"}>×</button>
        )
    }

    acceptColumnChoice = (v: ColumnClause) => {
        this.renderColumnChoice(v, false);
    }

    acceptLiteral = (v: LiteralClause) => {
        let vs: string = String(v.value);

        switch (v.type) {
            // TODO: numeric fields, date fields
            case ColumnType.Date:
                this.acc.push(<>TODO: date fields</>);
                break;
            case ColumnType.Int32:
                this.acc.push(
                    <SaneInput key={this.nextKey()} value={vs} onCommit={(s) => {
                        v.value = parseInt(s);
                        this.filter.onChanged();
                    }}></SaneInput>);
                break;
            case ColumnType.String:
            default:
                this.acc.push(
                    <SaneInput key={this.nextKey()} value={vs} onCommit={(s) => {
                        v.value = s;
                        this.filter.onChanged();
                    }}></SaneInput>

                )
        }
    }

    private renderColumnChoice(v: SearchClause | ColumnClause, root: boolean) {
        let className: string = "datatable-filter-select";

        /*
        if (this.props.ghost === true) {
            className = 'datatable-filter-create'
        } else {
            className = "";column_Parent column/Text
        } */

        let rootOption = []
        if (root === true) {
            rootOption.push(
                <option key={999} value={-1}>Search</option>
            );
        }

        let options = this.filter.query.columns.map(each =>
            <option key={each.columnNumber + 1000} value={each.name}>{each.name}</option>)

        this.acc.push(<select
            key={this.nextKey()}
            value={v.id} // TODO - 
            onChange={(ev) => this.filter.onColumnChange(ev.target.value, v.value)}
            className={className}>
            {rootOption.concat(options)}
        </select>);
    }
}
